import React, { useState, useRef, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { GiCrossedBones } from "react-icons/gi";
import { LuSave } from "react-icons/lu";
import { Grid, GridItem } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { SquarePen, Trash2 } from "lucide-react";
import ConfirmModal from "../../shared/ConfirmModal";
import { toast } from "react-toastify";

import {
    useGetGroupByIdQuery,
    useSaveGroupMutation,
    useGetCustomerByGroupIdQuery,
} from "../../../../services/redux/api/components";
import AreaDropdown from '../../../../utils/AreaDropdown';

const AddGroupMaster = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const [autoCode, setAutoCode] = useState("");
    const navigate = useNavigate();
    const gridRef = useRef();
    const [saveGroup] = useSaveGroupMutation();
    const { data: GroupData, isLoading } = useGetGroupByIdQuery(id, { skip: !isEditMode });
    const groupIdForFetch = GroupData?.data?.GroupID;

    const { data: customersByGroup, isLoading: isCustomersLoading } = useGetCustomerByGroupIdQuery(groupIdForFetch, {
        skip: !isEditMode || !groupIdForFetch,
    });

    const [rowData, setRowData] = useState([]);

    const apiRowData = useMemo(() => {
        const list = customersByGroup?.data || customersByGroup?.Data || [];
        return list.map((item) => {
            const firstName = item.FirstName || item.firstName || '';
            const middleName = item.MiddleName || item.middleName || '';
            const lastName = item.LastName || item.lastName || '';
            const fullName = item.FullName || item.fullName || `${firstName} ${middleName} ${lastName}`.trim();
            const dobRaw = item.DOB || item.dob || '';
            const dob = typeof dobRaw === 'string' && dobRaw.slice ? dobRaw.slice(0, 10) : dobRaw;
            return {
                customerId: item.CustomerID || item.customerId || item.Id || item.id,
                memberName: fullName,
                memberDOB: dob,
                relation: item.Relation || item.relation || '',
                mobileNo: item.Mobile || item.mobile || '',
                emailId: item.Email || item.email || '',
            };
        });
    }, [customersByGroup]);

    const initialValues = {
        GroupCode: GroupData?.data?.GroupCode || "",
        GroupName: GroupData?.data?.GroupName || "",
        AreaID: GroupData?.data?.AreaID ?? GroupData?.data?.AreaId ?? '',
    };

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState(null);

    function handleDelete(row) {
        setRowToDelete(row);
        setIsDeleteModalOpen(true);
    }

    function confirmDelete() {
        setRowData((prev) => prev.filter((r) => r !== rowToDelete));
        setIsDeleteModalOpen(false);
        setRowToDelete(null);
    }

    function cancelDelete() {
        setIsDeleteModalOpen(false);
        setRowToDelete(null);
    }

    const columnDefs = [
        { headerName: "Member Name", field: "memberName", flex: 1, headerClass: "ag-header-left" },
        { headerName: "Member DOB", field: "memberDOB", flex: 1, headerClass: "ag-header-left" },
        // { headerName: "Relation", field: "relation", flex: 1, headerClass: "ag-header-left" },
        { headerName: "Mobile No", field: "mobileNo", flex: 1, headerClass: "ag-header-left" },
        { headerName: "Email ID", field: "emailId", flex: 1, headerClass: "ag-header-left" },
        {
            headerName: "Action",
            field: "action",
            flex: 1,
            headerClass: "ag-header-left",
            cellRenderer: (params) => (
                <div style={{ display: "flex", gap: "8px" }}>
                    <button
                        type="button"
                        className="edit-button"
                        onClick={() => {
                            if (params?.data?.customerId) {
                                navigate(`/customer-master/edit/${params.data.customerId}`);
                            }
                        }}
                        title="Edit"
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <SquarePen color="#787474" size={18} className="mt-1" />
                    </button>
                    {/* <button
                        type="button"
                        className="edit-button"
                        onClick={() => { navigate(`/customer-master/edit/${params.data.customerId}`); }}
                        title="Edit"
                    >
                        <SquarePen />
                    </button> */}

                    <button onClick={() => handleDelete(params.data)}>
                        <Trash2 color="#787474" size={18} className="mt-1" />
                    </button>
                </div>
            ),
            sortable: false,
            filter: false,
        },
    ];

    // Auto-generate Group Code
    const generateGroupCode = () => {
        const randomCode = `GRP${Math.floor(1000 + Math.random() * 9000)}`;
        setAutoCode(randomCode);
        return randomCode;
    };

    const validationSchema = Yup.object({
        GroupCode: Yup.string().required("Group Code is required"),
        GroupName: Yup.string().required("Group Name is required"),
    });

    const handleSubmit = async (values, { resetForm }) => {
        // Ensure AreaID is numeric (send number or null to backend)
        const normalizedAreaId = (typeof values.AreaID !== 'undefined' && values.AreaID !== null && values.AreaID !== '') ? parseInt(values.AreaID, 10) : null;

        const payload = {
            ...values,
            AreaID: normalizedAreaId,
            Mode: isEditMode ? "Update" : "Add",
        };

        if (isEditMode) {
            payload.GroupID = id;
        }

        try {
            const response = await saveGroup(payload).unwrap();
            toast.success(
                response.message || (isEditMode ? "Group Updated Successfully" : "Group added successfully!")
            );
            resetForm();
            window.history.back();
        } catch (error) {
            console.error("Error during API call:", error);
            toast.error("An error occurred while saving the Group. Please try again.");
        }
    };

    return (
        <div className="formContainer lg:p-4 rounded" style={{ boxShadow: "0 0 8px #ccc" }}>
            <div className="flex flex-col lg:mx-5 p-4 border rounded bg-white" style={{ boxShadow: "0px 0px 12px #cac8c8" }}>
                <h5 className="text-brandRed font-bold border-b border-brandRed pb-2 mb-4 px-2">
                    {isEditMode ? "Edit Group Master" : "Add Group Master"}
                </h5>

                <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, setFieldValue, values }) => (
                        <Form>
                            {/* Group Code */}
                            <div className="row mb-3">
                                <div className="col-lg-6 col-md-8 col-sm-12">
                                    <label className="form-label">
                                        Group Code <span className="text-danger">*</span>
                                    </label>
                                    <div className="d-flex align-items-center gap-2">
                                        <Field
                                            name="GroupCode"
                                            className="form-control custom-shadow w-full px-3 py-2"
                                            style={{ height: "40px" }}
                                            value={autoCode || values.GroupCode}
                                            onChange={(e) => {
                                                setFieldValue("GroupCode", e.target.value);
                                                setAutoCode("");
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="backButton"
                                            style={{ height: "40px", width: "140px" }}
                                            onClick={() => {
                                                const code = generateGroupCode();
                                                setFieldValue("GroupCode", code);
                                            }}
                                        >
                                            Auto-Fill
                                        </button>
                                    </div>
                                    <ErrorMessage name="GroupCode" component="div" className="text-danger" />
                                </div>
                            </div>

                            {/* Group Name */}
                            <div className="row">
                                <div className="col-md-6">
                                    <label className="form-label">
                                        Group Name <span className="text-danger">*</span>
                                    </label>
                                    <Field
                                        type="text"
                                        name="GroupName"
                                        className="form-control custom-shadow w-full px-3 py-2"
                                    />
                                    <ErrorMessage name="GroupName" component="div" className="text-danger" />
                                </div>
                            </div>

                            {/* Area Select */}
                            <div className="row mt-3">
                                <div className="col-md-6">
                                    <AreaDropdown name="AreaID" />
                                </div>
                            </div>

                            {/* Group Member Section (Only in Edit Mode) */}
                            {isEditMode ? (
                                <>
                                    <Grid templateColumns="repeat(2, 2fr)" gap={5} className="mt-3">
                                        <GridItem className="text-gray-400 font-bold my-2 text-sm">
                                            Group Member Details
                                        </GridItem>
                                        <GridItem className="text-end">
                                            <button
                                                type="button"

                                                className="backButton"
                                                onClick={() =>
                                                    navigate("/customer-master/add", {
                                                        state: {
                                                            groupCode: values.GroupCode,
                                                            groupName: values.GroupName,
                                                            groupId: GroupData?.data?.GroupID,
                                                        },
                                                    })
                                                }
                                            >
                                                Add
                                            </button>
                                        </GridItem>
                                    </Grid>

                                    <div className="w-full overflow-x-auto" style={{ maxWidth: "100%" }}>
                                        <div
                                            className="ag-theme-alpine"
                                            style={{ minWidth: "700px", width: "100%", height: "250px", marginTop: "16px" }}
                                        >
                                            <AgGridReact
                                                ref={gridRef}
                                                rowData={apiRowData.length ? apiRowData : rowData}
                                                columnDefs={columnDefs}
                                                domLayout="autoHeight"
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center my-5">
                                    <p className="text-gray-500">
                                        Group Member Details will be available after saving the Group Master.
                                    </p>
                                </div>
                            )}

                            {/* Save & Cancel Buttons */}
                            <div className="d-flex justify-content-center gap-3 mt-5">
                                <button type="submit" className="saveButton d-flex" disabled={isSubmitting}>
                                    <span className="m-auto p-1">
                                        <LuSave />
                                    </span>
                                    {isSubmitting ? "Saving..." : "Save"}
                                </button>
                                <button
                                    type="button"
                                    className="backButton d-flex align-items-center"
                                    onClick={() => window.history.back()}
                                >
                                    <span className="m-auto p-1">
                                        <GiCrossedBones />
                                    </span>
                                    Cancel
                                </button>
                            </div>

                            {/* Confirm Delete Modal */}
                            <ConfirmModal
                                isOpen={isDeleteModalOpen}
                                onClose={cancelDelete}
                                onConfirm={confirmDelete}
                                title="Confirm Deletion"
                                message={
                                    rowToDelete
                                        ? `Are you sure you want to delete ${rowToDelete.memberName} member?`
                                        : "Are you sure you want to delete this member?"
                                }
                                confirmLabel="Delete"
                                cancelLabel="Cancel"
                                confirmColor="backButton"
                            />
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default AddGroupMaster;
